// Durable latest-wins delivery channel for viewport reference geometry.
//
// The exact geometry map changes on accepted source and follower slices. Passing
// that map through ViewerPage state re-rendered every diagnostic viewport merely
// to repaint a few SVG reference-line nodes. Silent replacement keeps the durable
// snapshot exact (including while REF is hidden); a coalesced notify wakes only
// the small ReferenceLineLayer subscribers.
export const VIEWPORT_REFERENCE_GEOMETRY_CHANNEL_MARKER = 'KAIROS_VIEWPORT_REFERENCE_GEOMETRY_CHANNEL';

export const EMPTY_REFERENCE_GEOMETRY_MAP = Object.freeze({});
const EMPTY_REFERENCE_GEOMETRY_SNAPSHOT = Object.freeze({
  version: 0,
  geometry: EMPTY_REFERENCE_GEOMETRY_MAP,
});

const normalizeGeometry = geometry => (
  geometry && typeof geometry === 'object' ? geometry : EMPTY_REFERENCE_GEOMETRY_MAP
);

export const createViewportReferenceGeometryChannel = () => {
  let snapshot = EMPTY_REFERENCE_GEOMETRY_SNAPSHOT;
  const subscribers = new Set();

  const notify = () => {
    subscribers.forEach(subscriber => {
      try { subscriber(); } catch (_) { /* isolate one overlay from every other pane */ }
    });
  };

  return {
    marker: VIEWPORT_REFERENCE_GEOMETRY_CHANNEL_MARKER,
    replaceSnapshot(geometry) {
      snapshot = {
        version: snapshot.version + 1,
        geometry: normalizeGeometry(geometry),
      };
      return snapshot;
    },
    notify,
    reset() {
      snapshot = {
        version: snapshot.version + 1,
        geometry: EMPTY_REFERENCE_GEOMETRY_MAP,
      };
      notify();
      return snapshot;
    },
    subscribe(subscriber) {
      if (typeof subscriber !== 'function') return () => {};
      subscribers.add(subscriber);
      return () => subscribers.delete(subscriber);
    },
    getSnapshot() {
      return snapshot;
    },
  };
};

// Stable no-channel fallbacks keep useSyncExternalStore hook order independent
// of REF visibility and channel presence.
export const emptyReferenceGeometrySubscribe = () => () => {};
export const emptyReferenceGeometrySnapshot = () => EMPTY_REFERENCE_GEOMETRY_SNAPSHOT;
